/* ============================================================
 * <product-countdown-popup>
 *   - Same open/close/session behaviour as <product-subscription-popup>.
 *   - Extra: live countdown timer that ticks every second.
 *   - When the deadline passes, behaviour is configured via
 *     data-expiry-behavior:
 *       'show_zeros'   -> keep the modal, show 00 across all units
 *       'hide_timer'   -> hide only the timer block
 *       'hide_popup'   -> unmount the whole popup
 * ============================================================ */

if (!customElements.get('product-countdown-popup')) {
  class ProductCountdownPopup extends HTMLElement {
    connectedCallback() {
      this.suppressionKey = this.dataset.suppressionKey || 'pcp-closed';
      this.showDelay = parseInt(this.dataset.showDelay, 10) || 0;
      this.expiryBehavior = this.dataset.expiryBehavior || 'show_zeros';
      this.manualOnly = this.dataset.manualOnly === 'true';

      // Always bind events + timer so the popup can be opened manually
      // (e.g. via the corner launcher) even after suppression.
      this.bindEvents();
      this.initTimer();

      // Auto-open only when not manual-only and not closed this session.
      if (this.manualOnly || this.isSuppressed()) return;

      this.scheduleOpen();
    }

    disconnectedCallback() {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      if (this.timerInterval) window.clearInterval(this.timerInterval);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    isSuppressed() {
      try {
        return window.sessionStorage.getItem(this.suppressionKey) === '1';
      } catch (e) {
        return false;
      }
    }

    markSuppressed() {
      try {
        window.sessionStorage.setItem(this.suppressionKey, '1');
      } catch (e) {
        /* private mode etc. — ignore */
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-pcp-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      const cta = this.querySelector('[data-pcp-cta]');
      if (cta) cta.addEventListener('click', (e) => this.handleCta(e));

      this.handleKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.handleKeydown);
    }

    scheduleOpen() {
      this.openTimer = window.setTimeout(() => this.open(), this.showDelay);
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-countdown-popup-open');
    }

    close() {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-countdown-popup-open');
      this.markSuppressed();
    }

    /* ----------------------- Countdown timer ----------------------- */

    initTimer() {
      this.timerBlock = this.querySelector('[data-countdown-block]');
      if (!this.timerBlock) return;

      const endStr = this.timerBlock.dataset.endDatetime;
      if (!endStr) return;

      const parsed = new Date(endStr);
      if (Number.isNaN(parsed.getTime())) {
        // Bad input — silently hide the timer so the popup still works.
        this.timerBlock.style.display = 'none';
        return;
      }

      this.endTime = parsed.getTime();
      this.timerUnits = {
        days: this.querySelector('[data-countdown-days]'),
        hours: this.querySelector('[data-countdown-hours]'),
        minutes: this.querySelector('[data-countdown-minutes]'),
        seconds: this.querySelector('[data-countdown-seconds]'),
      };

      this.updateTimer();
      this.timerInterval = window.setInterval(() => this.updateTimer(), 1000);
    }

    updateTimer() {
      if (!this.endTime) return;
      const diff = this.endTime - Date.now();

      if (diff <= 0) {
        this.setUnits({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        window.clearInterval(this.timerInterval);
        this.handleExpiry();
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      this.setUnits({ days, hours, minutes, seconds });
    }

    setUnits({ days, hours, minutes, seconds }) {
      const pad = (n) => String(n).padStart(2, '0');
      if (this.timerUnits.days) this.timerUnits.days.textContent = pad(days);
      if (this.timerUnits.hours) this.timerUnits.hours.textContent = pad(hours);
      if (this.timerUnits.minutes) this.timerUnits.minutes.textContent = pad(minutes);
      if (this.timerUnits.seconds) this.timerUnits.seconds.textContent = pad(seconds);
    }

    handleExpiry() {
      if (this.expiryBehavior === 'hide_timer' && this.timerBlock) {
        this.timerBlock.style.display = 'none';
      } else if (this.expiryBehavior === 'hide_popup') {
        // Cancel any pending open, mark suppressed, and remove from DOM.
        if (this.openTimer) window.clearTimeout(this.openTimer);
        this.markSuppressed();
        this.remove();
      }
      /* show_zeros: nothing else to do */
    }

    /* ----------------------- Primary CTA ----------------------- */

    handleCta(e) {
      e.preventDefault();

      // Select the SUBSCRIBE option on the PDP if present.
      const subscribeRadio = document.querySelector(
        'input[type="radio"][name="bundle_purchase_option"][value="subscribe"]',
      );
      if (subscribeRadio && !subscribeRadio.checked) {
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!subscribeRadio) {
        const legacySubBtn = document.querySelector(
          '.purchase-option-btn.purchase-option-btn--subscription',
        );
        if (legacySubBtn && !legacySubBtn.classList.contains('selected')) {
          legacySubBtn.click();
        }
      }

      this.close();

      window.requestAnimationFrame(() => {
        const atc =
          document.querySelector('[id^="AddToCart-"]') ||
          document.querySelector('button[name="add"]');
        if (atc) {
          atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
          try {
            atc.focus({ preventScroll: true });
          } catch (err) {
            /* older browsers */
          }
        }
      });
    }
  }

  customElements.define('product-countdown-popup', ProductCountdownPopup);
}
